import { Button, Modal, Typography } from "antd";
import { type ReactNode, useState } from "react";
import { useIntl } from "react-intl";
import {
  StyledDescription,
  StyledFormSection,
  StyledModalTitle,
  StyledTip,
  StyledTipsGrid,
  StyledTitle,
} from "./FormSection.styles";

export type FormSectionLabel = {
  titleIntlKey: string | { key: string; [variable: string]: string };
  descriptionIntlKey?: string | { key: string; [variable: string]: string };
};

type Props = {
  title: ReactNode;
  fields?: FormSectionLabel[];
};

const icon = <i className="fa-regular fa-circle-question" />;

export const FormSection = ({ title, fields }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const close = () => setIsModalOpen(false);

  const isManyFields = (fields?.length ?? 0) > 10;
  const fieldsHalfQty = Math.ceil((fields?.length ?? 0) / 2);
  const fieldsFirstHalf = isManyFields ? fields?.slice(0, fieldsHalfQty) : null;
  const fieldsSecondHalf = isManyFields ? fields?.slice(fieldsHalfQty) : null;

  const { formatMessage } = useIntl();

  const fieldMapFn = (field: FormSectionLabel, index: number) => {
    let titleIntKey = field.titleIntlKey;
    let titleIntlVariables: { [variable: string]: string } | undefined;

    if (typeof field.titleIntlKey === "object") {
      titleIntKey = field.titleIntlKey.key;
      titleIntlVariables = field.titleIntlKey;
    }

    let descriptionIntlKey = field.descriptionIntlKey;
    let descriptionIntlVariables: { [variable: string]: string } | undefined;

    if (typeof field.descriptionIntlKey === "object") {
      descriptionIntlKey = field.descriptionIntlKey.key;
      descriptionIntlVariables = field.descriptionIntlKey;
    }

    return (
      <StyledTip key={index}>
        <Typography.Text strong>{formatMessage({ id: titleIntKey as string }, titleIntlVariables)}</Typography.Text>
        <StyledDescription>
          {formatMessage({ id: descriptionIntlKey as string }, descriptionIntlVariables)}
        </StyledDescription>
      </StyledTip>
    );
  };

  return (
    <StyledFormSection>
      <StyledTitle level={5}>{title}</StyledTitle>

      {fields && (
        <>
          <Button icon={icon} type="text" size="small" onClick={() => setIsModalOpen(true)} />
          <Modal
            open={isModalOpen}
            onCancel={close}
            onOk={close}
            cancelButtonProps={{ hidden: true }}
            centered
            title={
              <StyledModalTitle>
                {icon}
                <span>{title}</span>
              </StyledModalTitle>
            }
          >
            <StyledTipsGrid>
              {!isManyFields ? (
                <div>{fields.map(fieldMapFn)}</div>
              ) : (
                <>
                  <div>{fieldsFirstHalf?.map(fieldMapFn)}</div>
                  <div>{fieldsSecondHalf?.map(fieldMapFn)}</div>
                </>
              )}
            </StyledTipsGrid>
          </Modal>
        </>
      )}
    </StyledFormSection>
  );
};
